import { Component, OnInit, ElementRef, ViewChild, OnDestroy, PLATFORM_ID, Inject, HostListener } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { News } from '../../core/models/news.interface';
import { NewsService } from '../../core/services/news.service';
import { forkJoin, catchError, of, Subject, timer, fromEvent } from 'rxjs';
import { CommonModule } from '@angular/common';
import { NewsGridComponent } from '../news-grid/news-grid.component';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { takeUntil, debounceTime, filter } from 'rxjs/operators';
import { ApiService } from '../../services/api.service';
import { ImageService } from '../../core/services/image.service';
import { SeoService } from '../../core/services/seo.service';


interface ErrorState {
  message: string;
}

interface Weather {
  temp: number | null;
  description: string;
  icon: string;
}

interface WeatherForecast extends Weather {
  date: Date;
}

interface WeatherData {
  current: Weather;
  forecast: WeatherForecast[];
}

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    NewsGridComponent,
    RouterModule
  ]
})
export class HomeComponent implements OnInit, OnDestroy {
  @ViewChild('storiesGrid') storiesGrid!: ElementRef;

  // Claves y duración del caché
  private readonly CACHE_KEY = 'homeCache';
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutos
  // Caducidad corta solo para el banner (evitar mostrar imagen vieja)
  // Desactivar cache prolongada del banner para evitar mostrar imágenes antiguas
  // Permitir reutilizar banner hasta 5 min para evitar espera inicial
  private readonly BANNER_CACHE_MAX_AGE = 5 * 60 * 1000; // 5 minutos

  isLoadingBanner = true;
  isLoadingWeather = true;
  isBannerImageLoaded = false;
  error: ErrorState | null = null;
  featuredArticle?: News;
  featuredArticles: News[] = [];
  latestArticles: News[] = [];
  cityNews: News[] = [];
  politicsNews: News[] = [];
  storiesNews: News[] = [];
  economyNews: News[] = [];
  sportsNews: News[] = [];
  worldNews: News[] = [];
  indArgNews: News[] = [];
  opinionArticle?: News;
  mainNews: News[] = [];
  weatherInfo: WeatherData | null = null;
  dolarRates: any[] = [];


  // Estados de carga para lazy loading
  sectionsLoaded = {
    city: false,
    politics: false,
    weather: false,
    stories: false,
    economy: false,
    sports: false,
    world: false,
    indarg: false
  };

  sectionsVisible = {
    city: false,
    politics: false,
    weather: false,
    stories: false,
    economy: false,
    sports: false,
    world: false,
    indarg: false
  };

  private destroy$ = new Subject<void>();
  private filterSubscription: any;
  private isDragging = false;
  private startX = 0;
  private scrollLeft = 0;
  private bannerImageVersion: string | null = null;
  private bannerObjectUrl: string | null = null;
  bannerSrc: string | null = null;
  filteredNews: News[] = [];
  showFilterResults = false;

  constructor(
    private newsService: NewsService,
    private apiService: ApiService,
    private imageService: ImageService,
    private seoService: SeoService,
    @Inject(PLATFORM_ID) private platformId: Object,
    private router: Router
  ) {}

  ngOnInit(): void {
    // Configurar SEO
    this.seoService.setDefaultMetaTags();
    
    // Cargar contenido inicial (banner inmediato)
    this.loadInitialContent();
    
    if (isPlatformBrowser(this.platformId)) {
      this.filterSubscription = fromEvent(document, 'filterNewsByTag')
        .pipe(takeUntil(this.destroy$))
        .subscribe((event: any) => {
          if (event.detail && event.detail.tag) {
            this.filterNewsByTag(event.detail.tag);
          }
        });

      // Iniciar sistema de carga por tiempo
      this.startTimedContentLoading();
      
      // Limpiar caché de imágenes expiradas al inicializar
      this.imageService.clearExpiredCache();
      // Limpiar caché de Firestore expirado
      this.newsService.clearExpiredFirestoreCache();
    }
    
    // Suscribirse a eventos de navegación para recargar datos al volver a la home
    this.router.events
      .pipe(
        takeUntil(this.destroy$),
        filter((event): event is NavigationEnd => event instanceof NavigationEnd)
      )
      .subscribe((event: NavigationEnd) => {
        // NavigationEnd indica que la navegación terminó
        if (this.router.url === '/' || this.router.url === '/la-ciudad') {
          // Solo recargar si han pasado más de 5 minutos desde la última carga
          const lastLoad = localStorage.getItem('lastHomeLoad');
          const now = Date.now();
          if (!lastLoad || (now - parseInt(lastLoad)) > this.CACHE_DURATION) {
            this.loadInitialContent();
            this.startTimedContentLoading();
            localStorage.setItem('lastHomeLoad', now.toString());
          }
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    
    if (isPlatformBrowser(this.platformId) && this.filterSubscription) {
      this.filterSubscription.unsubscribe();
    }

    // Liberar URL de objeto si existe
    if (isPlatformBrowser(this.platformId) && this.bannerObjectUrl) {
      try { URL.revokeObjectURL(this.bannerObjectUrl); } catch {}
      this.bannerObjectUrl = null;
    }
    
    // Limpiar caché al destruir el componente
    if (isPlatformBrowser(this.platformId)) {
      this.imageService.clearExpiredCache();
      this.newsService.clearExpiredFirestoreCache();
    }
  }

  private loadInitialContent(): void {
    const isBrowser = isPlatformBrowser(this.platformId); // Verifica si es el navegador

    // Limpiar caché de artículos destacados para asegurar que no se muestren noticias de 'ciudad'
    this.newsService.clearFeaturedCache();

    if (isBrowser) {
        // Mantener caché local para permitir carga instantánea del banner cuando exista
        const now = Date.now();
        const cachedData = localStorage.getItem(this.CACHE_KEY);

        // Detectar si es un reload duro para ignorar caché del banner
        let isReload = false;
        try {
          const nav = (performance as any)?.getEntriesByType?.('navigation') || [];
          isReload = !!nav[0] && nav[0].type === 'reload';
        } catch {}

        // Usar caché solo si es reciente y no es un reload duro
        if (cachedData && !isReload) {
            const { banner, featured, mainNews, timestamp } = JSON.parse(cachedData);
            const isFresh = !!timestamp && (now - timestamp) <= this.BANNER_CACHE_MAX_AGE;

            if (isFresh) {
              this.featuredArticle = banner;
              // Definir versión del banner para cache-busting consistente
              this.bannerImageVersion = this.computeBannerVersion(banner);
              this.updateBannerSrcFromArticle(banner);
              this.featuredArticles = featured || [];
              this.mainNews = mainNews || [];
              this.isLoadingBanner = false;
              this.isBannerImageLoaded = true; // mostrar banner de inmediato

              // Precargar en segundo plano
              this.preloadBannerImage(banner);
              this.preloadImages(this.featuredArticles);

              this.loadInitialSecondaryContent();
              this.loadFreshDataInBackground(); // Actualizar en segundo plano
              return;
            }
        }
    }

    // Si no hay caché válido o no es el navegador, cargar desde el servidor
    this.isLoadingBanner = true;
    this.isBannerImageLoaded = false;

    // Cargar destacado primero para liberar el banner lo antes posible
    this.newsService.getFeaturedArticles(true)
      .pipe(
        takeUntil(this.destroy$),
        catchError(error => {
          this.error = { message: error?.message || 'Error loading featured' };
          return of([]);
        })
      )
      .subscribe(featuredList => {
        if (featuredList && featuredList.length > 0) {
          const sortedArticles = this.sortArticles(featuredList);

          // Guardar en localStorage solo si es el navegador
          if (isPlatformBrowser(this.platformId)) {
            const cacheData = {
              banner: sortedArticles[0],
              featured: sortedArticles.slice(1, 4),
              mainNews: this.mainNews, // puede actualizarse luego
              timestamp: Date.now()
            };
            localStorage.setItem(this.CACHE_KEY, JSON.stringify(cacheData));
          }

          this.featuredArticle = sortedArticles[0];
          // Definir versión del banner para cache-busting consistente
          this.bannerImageVersion = this.computeBannerVersion(this.featuredArticle);
          this.updateBannerSrcFromArticle(this.featuredArticle);
          this.featuredArticles = sortedArticles.slice(1, 4);

          // Mostrar banner inmediatamente
          this.isLoadingBanner = false;
          this.isBannerImageLoaded = true;

          // Precargas en segundo plano
          this.preloadBannerImage(sortedArticles[0]);
          this.preloadImages(sortedArticles.slice(1));

          // Cargar contenido secundario sin bloquear el banner
          this.loadInitialSecondaryContent();
        } else {
          // Incluso si no hay destacados, liberar el banner para evitar bloqueo visual
          this.isLoadingBanner = false;
          this.isBannerImageLoaded = true;
        }
      });

    // En paralelo, cargar noticias principales sin bloquear el banner
    this.newsService.getMainNews(true)
      .pipe(
        takeUntil(this.destroy$),
        catchError(() => of([]))
      )
      .subscribe(mainNewsList => {
        this.mainNews = mainNewsList;

        // Actualizar caché con mainNews cuando llegue
        if (isPlatformBrowser(this.platformId)) {
          const currentCacheRaw = localStorage.getItem(this.CACHE_KEY);
          try {
            const currentCache = currentCacheRaw ? JSON.parse(currentCacheRaw) : {};
            const updatedCache = {
              ...currentCache,
              mainNews: mainNewsList,
              timestamp: Date.now()
            };
            localStorage.setItem(this.CACHE_KEY, JSON.stringify(updatedCache));
          } catch {}
        }
      });
}

/**
 * Sistema de carga por tiempo - reemplaza el Intersection Observer
 * Carga las secciones secuencialmente con intervalos de tiempo
 */
private startTimedContentLoading(): void {
  if (!isPlatformBrowser(this.platformId)) return;

  // Definir secuencia de carga con tiempos específicos
  const loadingSequence = [
    { section: 'actualidad', delay: 1000 }, // 1.5 segundos
    { section: 'latest-news', delay: 1500 }, // 2 segundos
    { section: 'politics', delay: 2300 }, // 2.5 segundos
    { section: 'weather', delay: 2800 }, // 3 segundos
    { section: 'stories', delay: 3000 }, // 3.5 segundos
    { section: 'economy', delay: 3200 }, // 9 segundos
    { section: 'sports', delay: 3500 }, // 10.5 segundos
    { section: 'world', delay: 3700 }, // 12 segundos
    { section: 'indarg', delay: 4000 } // 13.5 segundos
  ];

  // Programar la carga de cada sección
  loadingSequence.forEach(({ section, delay }) => {
    timer(delay).pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.loadSectionContent(section);
    });
  });
}

/**
 * Carga contenido secundario inicial - solo opinión para mostrar inmediatamente
 */
private loadInitialSecondaryContent(): void {
  // Solo cargar opinión inicialmente para mostrar algo mientras se cargan las demás secciones
  this.newsService.getArticlesByCategory('opinion', 1)
    .pipe(
      takeUntil(this.destroy$),
      catchError(error => {
        console.error('Error loading opinion article:', error);
        return of([]);
      })
    )
    .subscribe(results => {
      this.opinionArticle = results[0];
    });
}

/**
 * Carga contenido de secciones por tiempo - reemplaza el sistema de scroll
 */
private loadSectionContent(sectionId: string): void {
  if (this.sectionsLoaded[sectionId as keyof typeof this.sectionsLoaded]) {
    return;
  }

  console.log(`Cargando sección: ${sectionId}`);

  switch (sectionId) {
    case 'actualidad':
      this.newsService.getArticlesByCategory('actualidad', 3)
        .pipe(
          takeUntil(this.destroy$),
          catchError(error => {
            console.error('Error loading actualidad:', error);
            return of([]);
          })
        )
        .subscribe(results => {
          this.cityNews = results;
          this.sectionsLoaded.city = true;
          this.preloadImages(this.cityNews);
        });
      break;
    
    case 'latest-news':
      this.newsService.getLatestArticles(8)
        .pipe(
          takeUntil(this.destroy$),
          catchError(error => {
            console.error('Error loading latest news:', error);
            return of([]);
          })
        )
        .subscribe(results => {
          this.latestArticles = results;
          this.preloadImages(this.latestArticles);
        });
      break;
    
    case 'politics':
      this.newsService.getArticlesByCategory('politica', 3)
        .pipe(
          takeUntil(this.destroy$),
          catchError(error => {
            console.error('Error loading politics:', error);
            return of([]);
          })
        )
        .subscribe(results => {
          this.politicsNews = results;
          this.sectionsLoaded.politics = true;
          this.preloadImages(this.politicsNews);
        });
      break;
    
    case 'weather':
      this.loadWeather();
      this.loadDolarRates();
      break;
    
    case 'stories':
      this.newsService.getArticlesByCategory('historias', 5)
        .pipe(
          takeUntil(this.destroy$),
          catchError(error => {
            console.error('Error loading stories:', error);
            return of([]);
          })
        )
        .subscribe(results => {
          this.storiesNews = results;
          this.sectionsLoaded.stories = true;
          this.preloadImages(this.storiesNews);
        });
      break;
    
    case 'economy':
      this.newsService.getArticlesByCategory('economia', 3)
        .pipe(
          takeUntil(this.destroy$),
          catchError(error => {
            console.error('Error loading economy:', error);
            return of([]);
          })
        )
        .subscribe(results => {
          this.economyNews = results;
          this.sectionsLoaded.economy = true;
          this.preloadImages(this.economyNews);
        });
      break;
    
    case 'sports':
      this.newsService.getArticlesByCategory('deportes', 3)
        .pipe(
          takeUntil(this.destroy$),
          catchError(error => {
            console.error('Error loading sports:', error);
            return of([]);
          })
        )
        .subscribe(results => {
          this.sportsNews = results;
          this.sectionsLoaded.sports = true;
          this.preloadImages(this.sportsNews);
        });
      break;
    
    case 'world':
      this.newsService.getArticlesByCategory('mundo', 3)
        .pipe(
          takeUntil(this.destroy$),
          catchError(error => {
            console.error('Error loading world news:', error);
            return of([]);
          })
        )
        .subscribe(results => {
          this.worldNews = results;
          this.sectionsLoaded.world = true;
          this.preloadImages(this.worldNews);
        });
      break;
    
    case 'indarg':
      this.newsService.getArticlesByCategory('ind.arg', 3)
        .pipe(
          takeUntil(this.destroy$),
          catchError(error => {
            console.error('Error loading ind.arg:', error);
            return of([]);
          })
        )
        .subscribe(results => {
          this.indArgNews = results;
          this.sectionsLoaded.indarg = true;
          this.preloadImages(this.indArgNews);
        });
      break;
  }
}

private loadDolarRates() {
  (this.apiService as any).getDolarRates()
    .pipe(takeUntil(this.destroy$))
    .subscribe({
      next: (data: any) => {
        if (data) {
          this.dolarRates = data as any[];
        }
      },
      error: () => {
        // Puedes manejar el error si lo necesitas
      }
    });
}

private loadFreshDataInBackground(): void {
  const isBrowser = isPlatformBrowser(this.platformId); // Verifica si es el navegador

  forkJoin({
      featured: this.newsService.getFeaturedArticles(),
      mainNews: this.newsService.getMainNews()
  }).pipe(
      takeUntil(this.destroy$),
      catchError(() => of({ featured: [], mainNews: [] }))
  ).subscribe(results => {
      if (results.featured.length > 0) {
          const sortedArticles = this.sortArticles(results.featured);
          const newBanner = sortedArticles[0];

          // Guardar en localStorage solo si es el navegador
          if (isBrowser) {
              const cacheData = {
                  banner: newBanner,
                  featured: sortedArticles.slice(1, 4),
                  mainNews: results.mainNews,
                  timestamp: Date.now()
              };
              localStorage.setItem(this.CACHE_KEY, JSON.stringify(cacheData));
          }

          // Actualizar vista y banner inmediatamente si hay cambios
          if (!this.isLoadingBanner) {
              const bannerChanged = !this.featuredArticle || this.featuredArticle.id !== newBanner.id;
              this.featuredArticle = newBanner;
              this.featuredArticles = sortedArticles.slice(1, 4);
              this.mainNews = results.mainNews;

              if (bannerChanged) {
                  // Liberar object URL previo para no mantener la imagen anterior
                  if (isBrowser && this.bannerObjectUrl) {
                      try { URL.revokeObjectURL(this.bannerObjectUrl); } catch {}
                      this.bannerObjectUrl = null;
                  }
                  // Recalcular versión y actualizar src inmediatamente
                  this.bannerImageVersion = this.computeBannerVersion(newBanner);
                  this.updateBannerSrcFromArticle(newBanner);
                  // Precargar en segundo plano la nueva imagen
                  this.preloadBannerImage(newBanner);
              }
          }
      }
  });
}
  private sortArticles(articles: News[]): News[] {
    return [...articles].sort((a, b) => {
      const dateA = a.publishDate ? new Date(a.publishDate).getTime() : 0;
      const dateB = b.publishDate ? new Date(b.publishDate).getTime() : 0;
      return dateB - dateA;
    });
  }

  private preloadImages(articles: News[]): void {
    articles.forEach(article => {
      const imageUrl = this.imageService.getImageSrc(article);
      if (imageUrl) {
        this.imageService.checkImageExists(imageUrl);
      }
    });
  }

  private async preloadBannerImage(article: News): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      this.isBannerImageLoaded = true;
      return;
    }

    const baseUrl = this.imageService.getImageSrc(article);
    if (!baseUrl) {
      this.isBannerImageLoaded = true;
      return;
    }

    // Añadir timestamp para forzar bypass en proxies/CDN
    const tsSep = baseUrl.includes('?') ? '&' : '?';
    const tsUrl = `${baseUrl}${tsSep}ts=${Date.now()}`;

    // Limpiar URL anterior si existía
    if (this.bannerObjectUrl) {
      try { URL.revokeObjectURL(this.bannerObjectUrl); } catch {}
      this.bannerObjectUrl = null;
    }

    const controller = new AbortController();
    const timerId = setTimeout(() => controller.abort(), 8000); // 8s timeout
    try {
      // Intentar evitar caché del navegador
      const resp = await fetch(tsUrl, {
        cache: 'reload',
        headers: {
          'Cache-Control': 'no-cache, no-store, max-age=0',
          'Pragma': 'no-cache'
        },
        signal: controller.signal
      });
      clearTimeout(timerId);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      this.bannerObjectUrl = URL.createObjectURL(blob);
  // Actualizar src estable
  this.bannerSrc = this.bannerObjectUrl;
      this.isBannerImageLoaded = true;
    } catch {
      clearTimeout(timerId);
      // Fallback: marcar cargado y usar URL con timestamp en el template
  this.isBannerImageLoaded = true;
  // Intentar al menos setear un src versionado estable por fecha
  this.updateBannerSrcFromArticle(article);
    }
  }

  private loadWeather() {
    if (this.sectionsLoaded.weather) return;
    
    this.isLoadingWeather = true;
    this.apiService.getWeather()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          if (data) {
            this.weatherInfo = data;
          }
          this.isLoadingWeather = false;
          this.sectionsLoaded.weather = true;
        },
        error: () => {
          this.isLoadingWeather = false;
        }
      });
  }

  retryLoad(): void {
    this.error = null;
    this.loadInitialContent();
  }

  startDragging(event: MouseEvent) {
    this.isDragging = true;
    this.startX = event.pageX - this.storiesGrid.nativeElement.offsetLeft;
    this.scrollLeft = this.storiesGrid.nativeElement.scrollLeft;
  }

  stopDragging() {
    this.isDragging = false;
  }

  onDragging(event: MouseEvent) {
    if (!this.isDragging) return;
    event.preventDefault();
    const x = event.pageX - this.storiesGrid.nativeElement.offsetLeft;
    const walk = (x - this.startX) * 2;
    this.storiesGrid.nativeElement.scrollLeft = this.scrollLeft - walk;
  }

  getDayName(date: Date): string {
    return date.toLocaleDateString('es-ES', { weekday: 'short' }).toUpperCase();
  }

  private filterNewsByTag(tag: string) {
    const allNews = [
      ...this.cityNews,
      ...this.politicsNews,
      ...this.economyNews,
      ...this.sportsNews,
      ...this.worldNews,
      ...this.indArgNews
    ];

    const filteredNews = allNews.filter(news => 
      news.tags?.includes(tag) || 
      news.title.toLowerCase().includes(tag.toLowerCase()) ||
      (news.content && news.content.toLowerCase().includes(tag.toLowerCase()))
    );

    this.showFilteredNews(filteredNews);
  }

  private showFilteredNews(news: News[]) {
    this.filteredNews = news;
    this.showFilterResults = true;
  }

  getImageSrc(article: News, preferWebP: boolean = true): string | null {
    return this.imageService.getImageSrc(article, preferWebP);
  }

  /**
   * Obtiene la URL de la imagen con parámetro de cache-busting para evitar problemas de caché
   * @param article El artículo del que se quiere obtener la imagen
   * @returns La URL de la imagen con parámetro de versión único
   */
  getImageSrcWithCacheBusting(article: News): string | null {
    // Usar el nuevo método optimizado con caché
    return this.imageService.getImageSrc(article, true);
  }

  /**
   * Obtiene la URL del banner con cache-busting consistente
   */
  getBannerImageSrc(article: News): string | null {
    // Preferir un src ya resuelto y estable
    if (this.bannerSrc) return this.bannerSrc;
    // En primera pasada, computar uno estable por versión
    this.updateBannerSrcFromArticle(article);
    return this.bannerSrc;
  }

  private updateBannerSrcFromArticle(article: News | null | undefined): void {
    if (!article) {
      this.bannerSrc = null;
      return;
    }
    if (this.bannerObjectUrl) {
      this.bannerSrc = this.bannerObjectUrl;
      return;
    }
    const baseUrl = this.imageService.getImageSrc(article);
    if (!baseUrl) {
      this.bannerSrc = null;
      return;
    }
    const version = this.bannerImageVersion ?? this.computeBannerVersion(article);
    this.bannerImageVersion = version;
    const sep = baseUrl.includes('?') ? '&' : '?';
    this.bannerSrc = `${baseUrl}${sep}t=${version}`;
    // Preload la imagen para que el navegador empiece a descargarla de inmediato
    this.injectPreload(this.bannerSrc);
  }

  /**
   * Inyecta un <link rel="preload"> para adelantar la descarga de la imagen del banner
   */
  private injectPreload(url: string): void {
    if (!url) return;
    if (isPlatformBrowser(this.platformId)) {
      const exists = document.head.querySelector(`link[rel=\"preload\"][href=\"${url}\"]`);
      if (!exists) {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'image';
        link.href = url;
        document.head.appendChild(link);
      }
    }
  }

  /**
   * Calcula una versión numérica a partir de lastModified/publishDate.
   * Si no existe, usa el tiempo actual.
   */
  private computeBannerVersion(article: News): string {
    const toEpoch = (value: any): number | null => {
      try {
        if (!value) return null;
        if (value instanceof Date) return value.getTime();
        // Compatibilidad con Timestamp de Firestore
        if (typeof value.toDate === 'function') return value.toDate().getTime();
        if (typeof value === 'number') return value;
        if (typeof value === 'string') {
          const parsed = Date.parse(value);
          if (!Number.isNaN(parsed)) return parsed;
          const numeric = Number(value);
          return Number.isFinite(numeric) ? numeric : null;
        }
      } catch {
        return null;
      }
      return null;
    };

    const byLastModified = toEpoch((article as any).lastModified);
    const byPublish = toEpoch(article.publishDate as any);
    const version = byLastModified ?? byPublish ?? Date.now();
    return String(version);
  }
}